import { getProductImageURL } from '@noths/polaris-client-utils';
import type { ProductCarouselItem } from '@noths/polaris-design-system-candidate-components';
import type { Product } from '@noths/polaris-design-system-ooux-interfaces';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import type { ServiceRecommendedProduct } from 'src/builders/recommendedProduct';

/**
 * The fields the recommendations GA events report on, and nothing else.
 *
 * The carousel item the design system renders is a semantic `Product`, which
 * deliberately carries none of these, so tracking reads the service product
 * directly rather than the shape on screen. Narrow rather than the whole product
 * so the dependency is visible and a spec only has to build what it asserts on.
 */
export type RecommendationsTrackingProduct = Pick<
  ServiceRecommendedProduct,
  | 'code'
  | 'freeDomesticDelivery'
  | 'isNew'
  | 'onSale'
  | 'partner'
  | 'prices'
  | 'purchasable'
  | 'salePercentage'
  | 'title'
>;

/**
 * Adapts a listings-api recommendation into the OOUX `Product` the design system
 * consumes.
 *
 * The interface is deliberately semantic, so several service fields have no
 * counterpart and are not mapped: `salePercentage` because `ProductCard` derives
 * the discount by comparing `price` with `originalPrice`, `onSale` because the
 * discount is the pre-sale price being present, and `freeDomesticDelivery`
 * because the card expresses delivery as a cost and a date the recommendations
 * response does not carry. All of them still reach GA, which reads the service
 * product directly.
 *
 * Rating and the Most Loved badge are not mapped either, for a different reason:
 * the recommendations endpoint validates against a fixed schema that strips
 * `highly_rated` and the delivery attributes before they reach the response, so
 * there is nothing to map yet. DISC-4524 restores them.
 */
export const transformRecommendedProductToProduct = (
  {
    code,
    images,
    links,
    partner,
    preSalePrices,
    price,
    purchasable,
    title,
  }: ServiceRecommendedProduct,
  { currency }: { currency: CurrencyCode },
): Product => {
  const preSalePrice = preSalePrices?.find((preSale) => preSale.currency === price.currency);
  const [image] = images;

  return {
    /* Derived, not a constant. The request filters on the Vertex `availability`
       attribute rather than on `purchasable`, so nothing guarantees the two
       agree, and the card this replaces was given `purchasable` directly. */
    availability: purchasable ? 'in_stock' : 'out_of_stock',
    code: String(code),
    currencyCode: currency,
    /* The service returns a `{size}` token in place of a preset size, so the URL
       has to be resolved here: `ProductMedia.url` is contracted as a fully
       resolved URL and the card renders it verbatim, unlike the Ribbons card it
       replaces, which resolved the token itself. 'normal' is the size that card
       asked for, so the bytes on the wire do not change.

       Empty alt because the card wraps the image in a link named by the title,
       so describing it again would announce the product twice. */
    media: image ? [{ alt: '', type: 'image', url: getProductImageURL(image.href, 'normal') }] : [],
    /* Keyed on the pre-sale price alone, not on `onSale`. That matches the card
       this replaces, which took the pre-sale price and never received `onSale` at
       all, so a product priced down without the flag set keeps showing its
       discount. */
    ...(preSalePrice && { originalPrice: preSalePrice.amount }),
    // The card shows the partner's name as plain text. `shortcode` and `url` are
    // optional and unrendered, so they are left out until something needs them.
    partner: { name: partner.name },
    /* The single `price` field, not a lookup in `prices`. The request sets the
       currency, so this one is already quoted in it, which is what the adapter
       this replaces relied on. */
    price: price.amount,
    title,
    /* `rel`, not `href`. The recommendations response puts the site-relative
       product path in `rel` and an absolute URL in `href` that points at a QA
       host outside production. */
    url: links[0]?.rel || '',
  };
};

/**
 * One carousel item is one card's props: the product itself plus the flags that
 * are presentation rather than properties of the product.
 *
 * `priority` is deliberately unset. The carousel is client-fetched behind
 * `LazyRender`, so it is never above the fold and its images should not pre-empt
 * ones that are.
 */
export const transformRecommendedProductToProductCarouselItem = (
  product: ServiceRecommendedProduct,
  { currency }: { currency: CurrencyCode },
): ProductCarouselItem => ({
  isNew: product.isNew,
  product: transformRecommendedProductToProduct(product, { currency }),
  /* Constant, not `product.onSale`. Every discount the catalog expresses is a
     price reduction rather than a code-based offer, and the card this replaces
     styled all of them as a sale, so there is no per-product decision to make
     here. `promoteAsSale` does nothing on a product with no discount, so it does
     not need guarding. */
  promoteAsSale: true,
});
